function isValidYmd(year: number, month: number, day: number) {
  if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) return false
  if (year < 1900 || year > 2100 || month < 1 || month > 12 || day < 1 || day > 31) return false
  const date = new Date(year, month - 1, day)
  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day
}

function takeMonth(digits: string): { month: string; rest: string } {
  const first = digits[0]
  if (first === '0') {
    const month = digits.slice(0, Math.min(2, digits.length))
    return { month, rest: digits.slice(month.length) }
  }
  if (first === '1') {
    if (digits.length === 1) return { month: '1', rest: '' }
    const second = digits[1]
    if (second === '0' || second === '1' || second === '2') {
      return { month: digits.slice(0, 2), rest: digits.slice(2) }
    }
    return { month: '1', rest: digits.slice(1) }
  }
  return { month: first, rest: digits.slice(1) }
}

function takeDay(digits: string): { day: string; rest: string } {
  const first = digits[0]
  if (first === '0') {
    const day = digits.slice(0, Math.min(2, digits.length))
    return { day, rest: digits.slice(day.length) }
  }
  if (first === '1' || first === '2') {
    if (digits.length === 1) return { day: first, rest: '' }
    return { day: digits.slice(0, 2), rest: digits.slice(2) }
  }
  if (first === '3') {
    if (digits.length === 1) return { day: '3', rest: '' }
    if (digits[1] === '0' || digits[1] === '1') {
      return { day: digits.slice(0, 2), rest: digits.slice(2) }
    }
    return { day: '3', rest: digits.slice(1) }
  }
  return { day: first, rest: digits.slice(1) }
}

function maskDigitDate(digits: string): string {
  if (!digits) return ''
  const { month, rest: afterMonth } = takeMonth(digits)
  if (!afterMonth) return month
  const { day, rest: afterDay } = takeDay(afterMonth)
  if (!afterDay) return `${month}/${day}`
  return `${month}/${day}/${afterDay.slice(0, 4)}`
}

function splitDayAndYear(dayRaw: string): { day: string; year: string } {
  if (!dayRaw) return { day: '', year: '' }
  const first = dayRaw[0]
  if (first === '0') {
    return { day: dayRaw.slice(0, Math.min(2, dayRaw.length)), year: dayRaw.slice(2, 6) }
  }
  if (first === '1' || first === '2') {
    return { day: dayRaw.slice(0, Math.min(2, dayRaw.length)), year: dayRaw.slice(2, 6) }
  }
  if (first === '3') {
    if (dayRaw.length === 1) return { day: '3', year: '' }
    if (dayRaw[1] === '0' || dayRaw[1] === '1') {
      return { day: dayRaw.slice(0, 2), year: dayRaw.slice(2, 6) }
    }
    return { day: '3', year: dayRaw.slice(1, 5) }
  }
  return { day: first, year: dayRaw.slice(1, 5) }
}

function maskSlashedDate(source: string): string {
  const endsWithSlash = source.endsWith('/')
  const parts = source.split('/')
  const month = (parts[0] || '').replace(/\D/g, '').slice(0, 2)
  const dayRaw = (parts[1] || '').replace(/\D/g, '')
  const yearFromPart = (parts[2] || '').replace(/\D/g, '').slice(0, 4)
  if (!month) return endsWithSlash ? '/' : ''
  if (parts.length === 1) return month
  const { day, year: overflowYear } = splitDayAndYear(dayRaw)
  const year = yearFromPart || overflowYear
  if (parts.length >= 3) return `${month}/${day}/${year}`
  if (!day) return `${month}/`
  if (year) return `${month}/${day}/${year}`
  return `${month}/${day}${endsWithSlash ? '/' : ''}`
}

/** Inserts slashes as the user types. Accepts ISO `YYYY-MM-DD` and `M/D/YYYY`. */
export function maskUsDateInput(raw: string): string {
  const source = String(raw ?? '')
  const trimmed = source.trim()
  const iso = trimmed.match(/^(\d{4})-(\d{1,2})-(\d{1,2})(?:$|[T\s])/)
  if (iso) {
    return `${iso[2].padStart(2, '0')}/${iso[3].padStart(2, '0')}/${iso[1]}`
  }
  if (source.includes('/')) return maskSlashedDate(source)
  return maskDigitDate(source.replace(/\D/g, '').slice(0, 8))
}

/** `YYYY-MM-DD`, `''` if empty, or `null` if the text is not a real calendar date. */
export function toIsoDateOnly(raw: string): string | null {
  const trimmed = String(raw || '').trim()
  if (!trimmed) return ''
  const iso = trimmed.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/)
  const us = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/)
  let year: number
  let month: number
  let day: number
  if (iso) {
    year = Number(iso[1])
    month = Number(iso[2])
    day = Number(iso[3])
  } else if (us) {
    year = Number(us[3])
    month = Number(us[1])
    day = Number(us[2])
  } else {
    return null
  }
  if (!isValidYmd(year, month, day)) return null
  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
}

export function formatDateDisplay(value: string): string {
  const raw = String(value || '').trim().slice(0, 10)
  if (!raw) return ''
  const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (!match) return raw
  const [, year, month, day] = match
  return `${month}/${day}/${year}`
}
